import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Loader2, ChevronDown, ChevronRight, Settings2, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  getTableCatalog,
  buildTableCatalog,
  getCatalogGroups,
  updateTableCatalogMeta,
  type TableCatalogItem,
  type CatalogGroupItem,
  type BuildTableCatalogResult,
} from '../../services/data-integration-api';
import PipelineStatusBadge from '../../components/data-integration/PipelineStatusBadge';
import CatalogGroupPanel from '../../components/data-integration/CatalogGroupPanel';

interface TableCatalogProps {
  connectionId: string;
  onSelectTable?: (tableCatalogId: string) => void;
  onViewEr?: (connectionId: string) => void;
}

// 全量获取型录（循环分页，pageSize 上限 100）
async function fetchAllCatalog(connectionId: string, keyword?: string) {
  const all: TableCatalogItem[] = [];
  let page = 1;
  while (true) {
    const res = await getTableCatalog({ connectionId, keyword, page, pageSize: 100 });
    all.push(...res.items);
    if (all.length >= res.total) break;
    page++;
  }
  return all;
}

export default function TableCatalog({ connectionId, onSelectTable, onViewEr }: TableCatalogProps) {
  const [tables, setTables] = useState<TableCatalogItem[]>([]);
  const [groups, setGroups] = useState<CatalogGroupItem[]>([]);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);
  const [buildElapsed, setBuildElapsed] = useState(0);
  const buildTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [buildResult, setBuildResult] = useState<BuildTableCatalogResult | null>(null);

  // Grouped layout state
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showGroupPanel, setShowGroupPanel] = useState(false);

  // Inline editing state
  const [editingDisplayName, setEditingDisplayName] = useState<{ id: string; value: string } | null>(null);
  const [editingSyncOrder, setEditingSyncOrder] = useState<{ id: string; value: string } | null>(null);

  // Group section refs for scroll navigation
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const fetchGroups = useCallback(async () => {
    try {
      const result = await getCatalogGroups({ connectionId });
      setGroups(result.items);
    } catch {
      // Groups may not exist yet
      setGroups([]);
    }
  }, [connectionId]);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const all = await fetchAllCatalog(connectionId, keyword || undefined);
      setTables(all);
    } catch (e) {
      console.error('获取型录列表失败:', e);
    } finally {
      setLoading(false);
    }
  }, [connectionId, keyword]);

  useEffect(() => {
    fetchGroups();
    fetchCatalog();
  }, [fetchGroups, fetchCatalog]);

  const handleBuild = async () => {
    if (!window.confirm('确定构建/重建型录？')) return;
    setBuilding(true);
    setBuildResult(null);
    setBuildElapsed(0);
    buildTimerRef.current = setInterval(() => setBuildElapsed(t => t + 1), 1000);
    try {
      const result = await buildTableCatalog(connectionId);
      setBuildResult(result);
      fetchCatalog();
    } catch (e: any) {
      alert('型录构建失败: ' + e.message);
    } finally {
      if (buildTimerRef.current) clearInterval(buildTimerRef.current);
      buildTimerRef.current = null;
      setBuilding(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCatalog();
  };

  // ─── Inline edit handlers ───────────────────────────────────────

  const saveDisplayName = async (id: string, value: string) => {
    try {
      await updateTableCatalogMeta({ updates: [{ tableCatalogId: id, displayName: value }] });
      setTables(prev => prev.map(t => t._id === id ? { ...t, display_name: value } : t));
    } catch {
      toast.error('保存显示名称失败');
    }
    setEditingDisplayName(null);
  };

  const saveSyncOrder = async (id: string, raw: string) => {
    const num = raw.trim() === '' ? null : parseInt(raw, 10);
    if (raw.trim() !== '' && isNaN(num!)) {
      toast.error('请输入有效数字');
      setEditingSyncOrder(null);
      return;
    }
    try {
      await updateTableCatalogMeta({ updates: [{ tableCatalogId: id, syncOrder: num }] });
      setTables(prev => prev.map(t => t._id === id ? { ...t, sync_order: num } : t));
    } catch {
      toast.error('保存序号失败');
    }
    setEditingSyncOrder(null);
  };

  const changeGroup = async (tableId: string, newGroupId: string | null) => {
    try {
      await updateTableCatalogMeta({ updates: [{ tableCatalogId: tableId, groupId: newGroupId }] });
      setTables(prev => prev.map(t => t._id === tableId ? { ...t, group_id: newGroupId } : t));
      fetchGroups(); // refresh table_count
    } catch {
      toast.error('更新分组失败');
    }
  };

  // ─── Batch operations ──────────────────────────────────────────

  const batchChangeGroup = async (groupId: string | null) => {
    if (selectedIds.size === 0) return;
    const updates = Array.from(selectedIds).map(tableCatalogId => ({ tableCatalogId, groupId }));
    try {
      await updateTableCatalogMeta({ updates });
      setTables(prev => prev.map(t => selectedIds.has(t._id) ? { ...t, group_id: groupId } : t));
      setSelectedIds(new Set());
      fetchGroups();
      toast.success(`已移动 ${updates.length} 张表`);
    } catch {
      toast.error('批量更新分组失败');
    }
  };

  // ─── Toggle helpers ────────────────────────────────────────────

  const toggleCollapse = (groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids: string[]) => {
    const allSelected = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const scrollToGroup = (groupId: string) => {
    groupRefs.current[groupId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── Grouping logic ───────────────────────────────────────────

  const groupedTables: { groupId: string | null; groupName: string; sortOrder: number; items: TableCatalogItem[] }[] = [];

  // Named groups (sorted by sort_order)
  const sortedGroups = [...groups].sort((a, b) => a.sort_order - b.sort_order);
  for (const g of sortedGroups) {
    groupedTables.push({
      groupId: g._id,
      groupName: g.group_name,
      sortOrder: g.sort_order,
      items: tables.filter(t => t.group_id === g._id),
    });
  }

  // Ungrouped
  const ungrouped = tables.filter(t => !t.group_id);
  if (ungrouped.length > 0 || groups.length > 0) {
    groupedTables.push({
      groupId: null,
      groupName: '未分类',
      sortOrder: 9999,
      items: ungrouped,
    });
  }

  // If no groups exist, show flat list
  const showGroupedLayout = groups.length > 0;

  // ─── Table row renderer ───────────────────────────────────────

  const renderTableRow = (table: TableCatalogItem) => (
    <TableRow key={table._id} className="group">
      {/* Checkbox */}
      <TableCell className="w-8 px-2">
        <Checkbox
          checked={selectedIds.has(table._id)}
          onCheckedChange={() => toggleSelect(table._id)}
          onClick={e => e.stopPropagation()}
        />
      </TableCell>
      {/* Sync Order */}
      <TableCell className="w-16 px-2">
        {editingSyncOrder?.id === table._id ? (
          <Input
            type="number"
            value={editingSyncOrder.value}
            onChange={e => setEditingSyncOrder({ id: table._id, value: e.target.value })}
            onBlur={() => saveSyncOrder(table._id, editingSyncOrder.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveSyncOrder(table._id, editingSyncOrder.value); if (e.key === 'Escape') setEditingSyncOrder(null); }}
            className="h-7 w-14 text-center text-xs"
            autoFocus
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span
            className="inline-block w-14 text-center text-xs text-muted-foreground cursor-pointer hover:text-foreground rounded px-1 py-0.5 hover:bg-muted"
            onClick={e => { e.stopPropagation(); setEditingSyncOrder({ id: table._id, value: table.sync_order != null ? String(table.sync_order) : '' }); }}
            title="点击编辑序号"
          >
            {table.sync_order != null ? table.sync_order : <Hash className="inline h-3 w-3 opacity-30" />}
          </span>
        )}
      </TableCell>
      {/* Table Name */}
      <TableCell
        className="font-mono text-sm cursor-pointer hover:text-primary"
        onClick={() => onSelectTable?.(table._id)}
      >
        {table.table_name}
      </TableCell>
      {/* Display Name (inline edit) */}
      <TableCell className="max-w-[200px]">
        {editingDisplayName?.id === table._id ? (
          <Input
            value={editingDisplayName.value}
            onChange={e => setEditingDisplayName({ id: table._id, value: e.target.value })}
            onBlur={() => saveDisplayName(table._id, editingDisplayName.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveDisplayName(table._id, editingDisplayName.value); if (e.key === 'Escape') setEditingDisplayName(null); }}
            className="h-7 text-sm"
            autoFocus
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span
            className="inline-block w-full truncate cursor-pointer text-sm hover:text-foreground rounded px-1 py-0.5 hover:bg-muted"
            onClick={e => { e.stopPropagation(); setEditingDisplayName({ id: table._id, value: table.display_name || '' }); }}
            title={table.display_name || '点击编辑显示名称'}
          >
            {table.display_name || <span className="text-muted-foreground italic">未设置</span>}
          </span>
        )}
      </TableCell>
      {/* Row Count */}
      <TableCell className="text-right text-xs tabular-nums">{table.row_count_estimate?.toLocaleString()}</TableCell>
      {/* Column Count */}
      <TableCell className="text-center text-xs">{table.column_count}</TableCell>
      {/* Pipeline Status */}
      <TableCell className="px-2">
        <PipelineStatusBadge pipeline={table.pipeline} />
      </TableCell>
      {/* Group Assignment */}
      {showGroupedLayout && (
        <TableCell className="w-32 px-2" onClick={e => e.stopPropagation()}>
          <Select
            value={table.group_id || '__ungrouped__'}
            onValueChange={v => changeGroup(table._id, v === '__ungrouped__' ? null : v)}
          >
            <SelectTrigger className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ungrouped__">未分类</SelectItem>
              {sortedGroups.map(g => (
                <SelectItem key={g._id} value={g._id}>{g.group_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
      )}
    </TableRow>
  );

  // ─── Group section renderer ──────────────────────────────────

  const renderGroupSection = (section: typeof groupedTables[0]) => {
    const key = section.groupId || '__ungrouped__';
    const isCollapsed = collapsedGroups.has(key);
    const sectionIds = section.items.map(t => t._id);
    const allChecked = sectionIds.length > 0 && sectionIds.every(id => selectedIds.has(id));

    return (
      <div
        key={key}
        ref={el => { groupRefs.current[key] = el; }}
        className="mb-4"
      >
        {/* Group Header */}
        <div
          className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-t-md cursor-pointer select-none border-b border-border"
          onClick={() => toggleCollapse(key)}
        >
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span className="font-medium text-sm">{section.groupName}</span>
          <Badge variant="secondary" className="text-xs">{section.items.length}</Badge>
          {section.groupId && (
            <span className="text-xs text-muted-foreground ml-1">#{section.sortOrder}</span>
          )}
        </div>

        {/* Group Content */}
        {!isCollapsed && (
          <Table>
            <TableHeader>
              <TableRow className="text-xs">
                <TableHead className="w-8 px-2">
                  <Checkbox
                    checked={allChecked}
                    onCheckedChange={() => toggleSelectAll(sectionIds)}
                  />
                </TableHead>
                <TableHead className="w-16 px-2">序号</TableHead>
                <TableHead>表名</TableHead>
                <TableHead>显示名称</TableHead>
                <TableHead className="text-right">行数</TableHead>
                <TableHead className="text-center">字段</TableHead>
                <TableHead className="px-2">流程状态</TableHead>
                {showGroupedLayout && <TableHead className="w-32 px-2">分组</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {section.items.map(renderTableRow)}
              {section.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={showGroupedLayout ? 8 : 7} className="text-center py-4 text-muted-foreground text-sm">
                    暂无表
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* 构建型录遮罩进度弹窗 */}
      <Dialog open={building} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[360px]" onPointerDownOutside={e => e.preventDefault()} onEscapeKeyDown={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>正在构建型录</DialogTitle>
            <DialogDescription>正在读取三方数据库的表结构信息，请耐心等待...</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-6">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">已用时 {buildElapsed} 秒</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* 分组管理弹窗 */}
      <Dialog open={showGroupPanel} onOpenChange={setShowGroupPanel}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>分组管理</DialogTitle>
            <DialogDescription>创建、重命名或删除业务领域分组</DialogDescription>
          </DialogHeader>
          <CatalogGroupPanel
            connectionId={connectionId}
            groups={groups}
            onGroupsChange={() => { fetchGroups(); fetchCatalog(); }}
          />
        </DialogContent>
      </Dialog>

      {/* Top Bar */}
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <h2 className="text-base font-semibold">表型录</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowGroupPanel(true)}>
            <Settings2 className="h-4 w-4 mr-1" />分组管理
          </Button>
          {onViewEr && (
            <Button variant="outline" size="sm" onClick={() => onViewEr(connectionId)}>ER 关系图</Button>
          )}
          <Button size="sm" onClick={handleBuild} disabled={building}>
            {building ? '构建中...' : '构建型录'}
          </Button>
        </div>
      </div>

      <div className="p-6">
        {/* Build Result */}
        {buildResult && (
          <div className="bg-[var(--color-success-light)] rounded-md p-3 mb-4 text-sm">
            <p>型录构建完成 (版本 {buildResult.catalog_version})</p>
            <p>
              表: {buildResult.table_count} |
              字段: {buildResult.column_count} |
              外键: {buildResult.foreign_key_count} |
              耗时: {buildResult.duration_ms}ms
            </p>
            <p>新增: {buildResult.new_tables} | 更新: {buildResult.updated_tables} | 移除: {buildResult.removed_tables}</p>
          </div>
        )}

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-4 flex gap-2">
          <Input
            type="text"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            placeholder="搜索表名或显示名称..."
            className="flex-1"
          />
          <Button type="submit" variant="outline" size="sm">搜索</Button>
        </form>

        {/* Group Index Navigation */}
        {showGroupedLayout && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {groupedTables.map(section => (
              <Button
                key={section.groupId || '__ungrouped__'}
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => scrollToGroup(section.groupId || '__ungrouped__')}
              >
                {section.groupName}
                <Badge variant="secondary" className="ml-1 text-xs h-4 px-1">{section.items.length}</Badge>
              </Button>
            ))}
          </div>
        )}

        {/* Batch Action Bar */}
        {selectedIds.size > 0 && (
          <div className="mb-4 flex items-center gap-3 bg-muted/60 rounded-md px-4 py-2">
            <span className="text-sm font-medium">已选 {selectedIds.size} 张表</span>
            <Select onValueChange={v => batchChangeGroup(v === '__ungrouped__' ? null : v)}>
              <SelectTrigger className="h-7 w-40 text-xs">
                <SelectValue placeholder="移到分组..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__ungrouped__">未分类</SelectItem>
                {sortedGroups.map(g => (
                  <SelectItem key={g._id} value={g._id}>{g.group_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setSelectedIds(new Set())}>
              取消选择
            </Button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">加载中...</span>
          </div>
        ) : showGroupedLayout ? (
          // Grouped layout
          groupedTables.map(renderGroupSection)
        ) : (
          // Flat layout (no groups exist)
          <Table>
            <TableHeader>
              <TableRow className="text-xs">
                <TableHead className="w-8 px-2">
                  <Checkbox
                    checked={tables.length > 0 && tables.every(t => selectedIds.has(t._id))}
                    onCheckedChange={() => toggleSelectAll(tables.map(t => t._id))}
                  />
                </TableHead>
                <TableHead className="w-16 px-2">序号</TableHead>
                <TableHead>表名</TableHead>
                <TableHead>显示名称</TableHead>
                <TableHead className="text-right">行数</TableHead>
                <TableHead className="text-center">字段</TableHead>
                <TableHead className="px-2">流程状态</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tables.map(renderTableRow)}
              {tables.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                    暂无型录数据，请先构建型录
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {/* Total count */}
        {!loading && tables.length > 0 && (
          <div className="mt-3 text-xs text-muted-foreground">
            共 {tables.length} 张表{groups.length > 0 ? ` · ${groups.length} 个分组` : ''}
          </div>
        )}
      </div>
    </div>
  );
}
